import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Free-text list input rendered as removable chips.
 *
 * Lets a school add and remove values one at a time — contact types, permitted email domains — rather than
 * typing comma-separated text, which is what the API expects to receive as an array anyway.
 */
@Component({
  selector: 'sb-tag-input',
  templateUrl: './tag-input.component.html',
  styleUrls: ['./tag-input.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagInputComponent),
      multi: true
    }
  ]
})
export class TagInputComponent implements ControlValueAccessor {
  @Input() placeholder = 'SB_Add_A_Value';
  @Input() inputId: string;
  @Input() ariaLabel: string;

  values: string[] = [];
  pending = '';
  isDisabled = false;

  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string[]): void {
    this.values = value ? [...value] : [];
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commitPending();
      return;
    }

    // Backspace on an empty box removes the last chip, so a mistyped value can be undone without reaching
    // for the mouse.
    if (event.key === 'Backspace' && !this.pending && this.values.length) {
      this.remove(this.values.length - 1);
    }
  }

  onBlur(): void {
    this.commitPending();
    this.onTouched();
  }

  remove(index: number): void {
    if (this.isDisabled) {
      return;
    }

    this.values.splice(index, 1);
    this.onChange([...this.values]);
  }

  /**
   * Commits whatever is in the box. Pasted comma-separated text is split, so a value copied out of the old
   * Support screen still lands as separate chips.
   */
  private commitPending(): void {
    if (this.isDisabled) {
      return;
    }

    const added = this.pending
      .split(',')
      .map(value => value.trim())
      .filter(value => value.length > 0 && !this.values.includes(value));

    this.pending = '';

    if (!added.length) {
      return;
    }

    this.values = [...this.values, ...added];
    this.onChange([...this.values]);
  }
}
